import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  Microphone, 
  PaperPlaneRight,
  Paperclip,
  Camera,
  Smiley,
  X,
  Trash,
  Sticker,
  FloppyDisk,
  LockSimple,
  LockSimpleOpen
} from '@phosphor-icons/react';
import EmojiPicker from 'emoji-picker-react';
import axios from 'axios';
import StickersPanel from './StickersPanel';
import SavedTextsPanel from './SavedTextsPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhatsAppInput = ({
  onSendMessage,
  onSendMedia,
  onOpenMediaPicker,
  onTyping,
  placeholder = "Type a message...",
  disabled = false,
  initialMessage = "",
  replyingTo = null,
  onCancelReply = () => {},
  userId = null,
  userRole = 'customer' // customer, handler, admin
}) => {
  const [message, setMessage] = useState(initialMessage);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showStickers, setShowStickers] = useState(false);
  const [showSavedTexts, setShowSavedTexts] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [waveformData, setWaveformData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  // Point 64 — press-and-hold recording with slide-up-to-lock.
  const [isLocked, setIsLocked] = useState(false);
  const [dragUpPx, setDragUpPx] = useState(0);
  const holdStartYRef = useRef(0);
  const isLockedRef = useRef(false); // read inside the pointerup handler without a stale closure
  const recordingActiveRef = useRef(false); // guard: prevent duplicate MediaRecorder starts (touch + emulated mouse)
  const isRecordingRef = useRef(false); // read recording state without stale closure
  const cancelledRef = useRef(false); // set when user taps trash — onstop must NOT send
  const lastTouchTimeRef = useRef(0);  // suppress synthetic mousedown that follows any touch
  const simpleVoice = userRole !== 'customer'; // admin/handler: simple press-hold voice, "Message" placeholder, emoji-panel fixes
  const effectivePlaceholder = simpleVoice ? 'Message' : placeholder;
  
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const streamRef = useRef(null);
  const recordingTimeRef = useRef(0);

  // Update message when initialMessage changes
  useEffect(() => {
    if (initialMessage && !message) {
      setMessage(initialMessage);
    }
  }, [initialMessage, message]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (audioContextRef.current) {
        try { audioContextRef.current.close(); } catch(e) {}
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Handle emoji selection
  const onEmojiClick = (emojiObject) => {
    setMessage(prev => prev + emojiObject.emoji);
    if (simpleVoice) {
      // WhatsApp behavior: keep the emoji panel open and don't open the native keyboard
      return;
    }
    setShowEmojiPicker(false);
    inputRef.current?.focus();
  };

  // Send text message
  const handleSend = () => {
    if (message.trim() && onSendMessage) {
      const currentInput = inputRef.current;
      const textToSend = message.trim();
      
      // Reset height BEFORE clearing message (prevents layout shift)
      if (currentInput) {
        currentInput.style.height = '40px';
      }
      
      // Clear message state
      setMessage('');
      setShowEmojiPicker(false);
      
      // Send the message
      onSendMessage(textToSend);
      
      // Keep focus without losing keyboard (prevents bounce)
      if (currentInput) {
        // Use preventScroll to avoid any viewport shifts
        currentInput.focus({ preventScroll: true });
      }
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle input change with typing indicator
  const handleInputChange = (e) => {
    setMessage(e.target.value);
    if (onTyping) {
      onTyping(true);
    }
  };

  // Handle sticker selection
  const handleStickerSelect = (sticker) => {
    if (onSendMessage) {
      // Generate a client-side UUID for server-side dedup — prevents the
      // "double-tap = 2 stickers" bug on slow (production) networks.
      const clientMessageId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `cm-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      // Anything that looks like a media/data URL → send as sticker media.
      // (Data-URL prefix covers our SVG built-in stickers.)
      if (sticker.startsWith('http') || sticker.startsWith('/') || sticker.startsWith('data:')) {
        onSendMedia && onSendMedia({
          type: 'sticker',
          url: sticker,
          client_message_id: clientMessageId,
        });
      } else {
        // Emoji sticker - send as text
        onSendMessage(sticker, { client_message_id: clientMessageId });
      }
    }
    setShowStickers(false);
  };

  // Handler / Admin get access to the Stickers picker. Customers don't.
  const canUseStickers = ['handler', 'admin', 'super_admin', 'deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'third_party_withdraw_handler'].includes(userRole);

  // Handle saved text selection
  const handleSavedTextSelect = (text) => {
    if (onSendMessage) {
      onSendMessage(text);
    }
    setShowSavedTexts(false);
  };

  // Update waveform visualization
  const updateWaveform = useCallback(() => {
    if (!analyserRef.current) return;
    
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);
    
    // Sample 30 bars for waveform
    const samples = 30;
    const blockSize = Math.floor(dataArray.length / samples);
    const bars = [];
    
    for (let i = 0; i < samples; i++) {
      let sum = 0;
      for (let j = 0; j < blockSize; j++) {
        sum += dataArray[i * blockSize + j];
      }
      const average = sum / blockSize;
      bars.push(Math.max(4, (average / 255) * 40));
    }
    
    setWaveformData(bars);
    animationRef.current = requestAnimationFrame(updateWaveform);
  }, []);

  // Start voice recording with waveform
  const startRecording = async () => {
    if (recordingActiveRef.current) return; // prevent duplicate starts
    recordingActiveRef.current = true;
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Voice recording not supported in this browser');
        recordingActiveRef.current = false;
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      // Setup audio context for waveform
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;
      
      // Setup MediaRecorder
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        recordingActiveRef.current = false;
        isRecordingRef.current = false;
        const wasCancelled = cancelledRef.current;
        cancelledRef.current = false;
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        
        // Auto-upload and send (WhatsApp instant-send behavior) — but NEVER
        // when the user cancelled via the trash button.
        if (!wasCancelled && onSendMedia && blob.size > 0) {
          setIsUploading(true);
          try {
            const formData = new FormData();
            formData.append('file', blob, 'voice.webm');
            formData.append('media_type', 'voice');
            const response = await axios.post(`${API}/upload/media`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
            onSendMedia({
              type: 'voice',
              url: response.data.url,
              duration: recordingTimeRef.current,
              view_once: false
            });
          } catch (error) {
            console.error('Failed to upload voice:', error);
            toast.error('Voice message send failed');
          } finally {
            setIsUploading(false);
          }
        }
        
        // Reset state
        setRecordingTime(0);
        recordingTimeRef.current = 0;
        setWaveformData([]);
        isLockedRef.current = false;
        setIsLocked(false);
        setDragUpPx(0);
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      isRecordingRef.current = true;
      cancelledRef.current = false;
      setRecordingTime(0);
      
      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          recordingTimeRef.current = newTime;
          return newTime;
        });
      }, 1000);
      
      // Start waveform animation
      updateWaveform();
      
    } catch (error) {
      recordingActiveRef.current = false;
      isRecordingRef.current = false;
      console.error('Failed to start recording:', error);
      
      let errorMessage = 'Microphone access denied.\n\n';
      if (error.name === 'NotAllowedError') {
        errorMessage += 'Please allow microphone permission in your browser settings.';
      } else if (error.name === 'NotFoundError') {
        errorMessage += 'No microphone found.';
      } else {
        errorMessage += `Error: ${error.message}`;
      }
      toast.error(errorMessage);
    }
  };

  // Stop recording (auto-sends via onstop callback)
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      isRecordingRef.current = false;
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }
  };

  // Cancel recording
  const cancelRecording = () => {
    // Flag so the async onstop handler discards the audio instead of sending.
    cancelledRef.current = true;
    audioChunksRef.current = [];
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setRecordingTime(0);
    recordingTimeRef.current = 0;
    setWaveformData([]);
    isLockedRef.current = false;
    setIsLocked(false);
    setDragUpPx(0);
    recordingActiveRef.current = false;
    isRecordingRef.current = false;
  };

  const LOCK_THRESHOLD_PX = 80;

  // Track ANY touch on the page so a synthetic mousedown that follows it (the
  // classic cause of a phantom recording after back-navigation) is suppressed.
  useEffect(() => {
    const markTouch = () => { lastTouchTimeRef.current = Date.now(); };
    document.addEventListener('touchstart', markTouch, { passive: true, capture: true });
    return () => document.removeEventListener('touchstart', markTouch, { capture: true });
  }, []);

  // Point 64 — hold-to-record: press starts recording immediately; sliding
  // the finger/pointer up past the threshold locks it into hands-free
  // recording (release no longer stops it); releasing before the threshold
  // stops and sends, matching WhatsApp.
  const handleMicHoldStart = (e) => {
    // Bug fix — a synthetic `mousedown` fires right after a touch (e.g. just
    // after tapping BACK), which was starting a phantom recording. Ignore any
    // mousedown that closely follows a touch, and non-primary mouse buttons.
    if (e.type === 'touchstart') {
      lastTouchTimeRef.current = Date.now();
    } else if (e.type === 'mousedown') {
      if (e.button !== 0) return;
      if (Date.now() - lastTouchTimeRef.current < 900) return;
    }
    // Never double-start.
    if (isRecordingRef.current || recordingActiveRef.current) return;

    if (simpleVoice) {
      // Admin/Handler: press mic to START recording. Recording continues after
      // release; the user taps the green Send button to send (or trash to
      // cancel). A stray tap therefore never fires off a voice message.
      if (e.cancelable) e.preventDefault();
      startRecording();
      return;
    }
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    holdStartYRef.current = y;
    isLockedRef.current = false;
    setIsLocked(false);
    setDragUpPx(0);
    startRecording();
  };

  useEffect(() => {
    if (!isRecording) return;
    const handleMove = (e) => {
      if (simpleVoice) return; // no slide-to-lock in simple mode
      if (isLockedRef.current) return;
      const y = e.touches ? e.touches[0].clientY : e.clientY;
      const delta = holdStartYRef.current - y; // positive = moved up
      const clamped = Math.max(0, Math.min(delta, LOCK_THRESHOLD_PX));
      setDragUpPx(clamped);
      if (delta >= LOCK_THRESHOLD_PX) {
        isLockedRef.current = true;
        setIsLocked(true);
      }
    };
    const handleRelease = () => {
      if (simpleVoice) return; // admin/handler: recording continues until Send/Cancel is tapped
      if (!isLockedRef.current) {
        stopRecording(); // release-to-send, mic wasn't locked
      }
      // Locked: keep recording hands-free until the explicit send/cancel button.
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('touchmove', handleMove, { passive: true });
    document.addEventListener('mouseup', handleRelease);
    document.addEventListener('touchend', handleRelease);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('mouseup', handleRelease);
      document.removeEventListener('touchend', handleRelease);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording]);

  // Format recording time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Voice Recording UI - Only shows during active recording
  if (isRecording) {
    return (
      <div className="bg-white border-t p-3">
        {/* Reply Preview in recording mode */}
        {replyingTo && (
          <div className="bg-gray-100 p-2 mb-2 border-l-4 border-green-600 flex justify-between items-start rounded text-sm">
            <div className="flex-1 truncate">
              <span className="text-green-600 font-semibold text-xs">Replying to {replyingTo.sender_name}</span>
              <p className="text-gray-600 truncate">{replyingTo.text || 'Photo'}</p>
            </div>
            <button onClick={onCancelReply} className="text-gray-500 ml-2" data-testid="reply-cancel-btn-recording"><X size={16} /></button>
          </div>
        )}
        
        {/* Point 64 — slide-up-to-lock indicator. Shows progress toward the
            lock threshold while held; once locked, recording continues
            hands-free and this becomes a static "Locked" pill. */}
        {!simpleVoice && !isLocked && (
          <div
            className="flex items-center justify-center gap-1 text-xs text-gray-500 mb-1 transition-transform"
            style={{ transform: `translateY(-${dragUpPx}px)` }}
            data-testid="voice-lock-hint"
          >
            <LockSimpleOpen size={14} className={dragUpPx > LOCK_THRESHOLD_PX * 0.6 ? 'text-green-600' : 'text-gray-400'} />
            <span>{dragUpPx > LOCK_THRESHOLD_PX * 0.6 ? 'Release to lock' : 'Slide up to lock'}</span>
          </div>
        )}
        {!simpleVoice && isLocked && (
          <div className="flex items-center justify-center gap-1 text-xs font-semibold text-green-600 mb-1" data-testid="voice-locked-indicator">
            <LockSimple size={14} weight="fill" />
            <span>Locked — recording hands-free</span>
          </div>
        )}

        <div className="flex items-center gap-3 bg-green-50 rounded-full px-4 py-2">
          {/* Cancel Button */}
          <button
            onClick={cancelRecording}
            className="p-2 text-red-500 hover:bg-red-100 rounded-full"
            data-testid="cancel-recording-btn"
          >
            <Trash size={24} weight="bold" />
          </button>

          {/* Live Waveform */}
          <div className="flex-1 flex items-center gap-2">
            <div className="flex items-center gap-0.5 h-10 flex-1">
              {waveformData.map((height, i) => (
                <div
                  key={i}
                  className="bg-green-500 rounded-full w-1"
                  style={{ height: `${height}px`, transition: 'height 0.1s ease' }}
                />
              ))}
            </div>
            {/* Recording indicator */}
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <span className="text-sm font-mono text-gray-600">{formatTime(recordingTime)}</span>
            </div>
          </div>

          {/* Stop & Send Button (instant send on stop) */}
          <button
            onClick={stopRecording}
            className="p-3 bg-green-500 text-white rounded-full"
            data-testid="stop-send-recording-btn"
          >
            <PaperPlaneRight size={24} weight="fill" />
          </button>
        </div>
        
        {/* Uploading indicator */}
        {isUploading && (
          <div className="flex items-center justify-center gap-2 mt-2 text-sm text-gray-500">
            <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
            Sending...
          </div>
        )}
      </div>
    );
  }

  // Normal Input UI
  return (
    <div className="bg-[#f0f2f5] px-2 py-[5px] relative" style={{ paddingBottom: 'max(5px, env(safe-area-inset-bottom))' }}>
      {/* Saved Texts Panel (Handler/Admin only) — kept as overlay */}
      {showSavedTexts && (userRole === 'handler' || userRole === 'admin') && (
        <SavedTextsPanel
          userId={userId}
          userRole={userRole}
          onTextSelect={handleSavedTextSelect}
          onClose={() => setShowSavedTexts(false)}
        />
      )}

      {/* Reply Preview */}
      {replyingTo && (
        <div className="bg-gray-100 p-2 mb-2 border-l-4 border-green-600 flex justify-between items-start rounded">
          <div className="flex-1">
            <div className="text-xs text-green-600 font-semibold">
              Replying to {replyingTo.sender_name}
            </div>
            <div className="text-sm text-gray-700 truncate">
              {replyingTo.text || 'Photo'}
            </div>
          </div>
          <button
            onClick={onCancelReply}
            className="text-gray-500 hover:text-gray-700 ml-2"
            data-testid="reply-cancel-btn"
          >
            <X size={20} weight="bold" />
          </button>
        </div>
      )}

      <div className="flex items-center gap-2" style={{maxWidth: '100vw', overflow: 'hidden'}}>
        {/* WhatsApp-style input container */}
        <div className="flex-1 flex items-center bg-[#f0f2f5] rounded-full px-1 min-h-[44px] min-w-0">
          {/* Emoji Button */}
          <button
            onClick={() => {
              const next = !showEmojiPicker;
              setShowEmojiPicker(next);
              setShowStickers(false);
              setShowSavedTexts(false);
              if (simpleVoice && next) {
                // Open only the emoji panel — close the native keyboard, keep header in place
                inputRef.current?.blur();
              }
            }}
            className="p-2 text-[#54656f] hover:text-[#00a884] flex-shrink-0"
            data-testid="emoji-btn"
          >
            <Smiley size={24} />
          </button>

          {/* Sticker Button (handler + admin only) — sits right next to the
              emoji button so it feels like part of the same expression picker. */}
          {canUseStickers && (
            <button
              onClick={() => {
                setShowStickers(v => !v);
                setShowEmojiPicker(false);
                setShowSavedTexts(false);
              }}
              className="p-2 text-[#54656f] hover:text-[#00a884] flex-shrink-0"
              data-testid="sticker-btn"
              title="Stickers"
            >
              <Sticker size={22} />
            </button>
          )}

          {/* Text Input */}
          <textarea
            ref={inputRef}
            value={message}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            onFocus={() => { if (simpleVoice) setShowEmojiPicker(false); }}
            placeholder={effectivePlaceholder}
            disabled={disabled}
            rows={1}
            style={{ maxHeight: '120px', minHeight: '40px', resize: 'none' }}
            onInput={(e) => {
              e.target.style.height = '40px';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            className="flex-1 bg-transparent py-2.5 px-1 focus:outline-none text-[15px] min-w-0 overflow-y-auto text-gray-900 placeholder-[#8696a0]"
            data-testid="chat-input"
          />

          {/* Attachment & Camera - hide when typing */}
          {!message.trim() && (
            <>
              <button
                onClick={() => {
                  setShowEmojiPicker(false);
                  setShowStickers(false);
                  setShowSavedTexts(false);
                  if (onOpenMediaPicker) onOpenMediaPicker();
                }}
                className="p-2 text-[#54656f] hover:text-[#00a884] flex-shrink-0"
                data-testid="media-picker-btn"
              >
                <Paperclip size={22} />
              </button>
              <button
                className="p-2 text-[#54656f] hover:text-[#00a884] flex-shrink-0"
                data-testid="camera-btn"
                onClick={() => {
                  // Open the media picker (attachment dialog) instead of direct upload
                  if (onOpenMediaPicker) onOpenMediaPicker();
                }}
              >
                <Camera size={22} />
              </button>
            </>
          )}
        </div>

        {/* Send or Voice Button - ALWAYS visible, never hidden */}
        {message.trim() ? (
          <button
            onClick={handleSend}
            className="w-[48px] h-[48px] bg-[#00a884] text-white rounded-full hover:bg-[#008f72] flex-shrink-0 flex items-center justify-center"
            data-testid="send-message-btn"
          >
            <PaperPlaneRight size={22} weight="fill" />
          </button>
        ) : (
          <>
            <button
              onMouseDown={handleMicHoldStart}
              onTouchStart={handleMicHoldStart}
              className="w-[48px] h-[48px] bg-[#00a884] text-white rounded-full hover:bg-[#008f72] flex-shrink-0 flex items-center justify-center touch-none select-none"
              title={simpleVoice ? 'Hold to record' : 'Hold to record — slide up to lock hands-free'}
              data-testid="start-recording-btn"
            >
              <Microphone size={22} weight="fill" />
            </button>
          </>
        )}
      </div>

      {/* Inline Picker Dock — WhatsApp-style: opens below the input row and
          pushes the chat area up. Only one picker visible at a time. */}
      {showEmojiPicker && (
        <div className="mt-1 border-t border-gray-200 bg-white" data-testid="emoji-picker-inline">
          <EmojiPicker
            onEmojiClick={onEmojiClick}
            height={320}
            width="100%"
            searchDisabled={false}
            autoFocusSearch={!simpleVoice}
            previewConfig={{ showPreview: false }}
          />
        </div>
      )}
      {showStickers && (
        <div className="mt-1 border-t border-gray-200 bg-white" data-testid="stickers-picker-inline">
          <StickersPanel
            inline
            userId={userId}
            userRole={userRole}
            onStickerSelect={handleStickerSelect}
            onClose={() => setShowStickers(false)}
          />
        </div>
      )}
    </div>
  );
};

export default WhatsAppInput;
